import {ensureFormCsrfToken} from './csrf';
import {fetchJson, getElementById} from './dom';

interface CbarDialogElement extends HTMLElement {
    open: boolean;
    close(returnValue?: string): void;
}

type UploadPermissionResponse = {
    result: string;
    message?: string;
    data?: {
        entity_name: string;
        entity_title: string;
    };
};

type RemoveEntityFileResponse = {
    result: string;
    message?: string;
};

function getUploadDialog(): CbarDialogElement | null {
    return getElementById('modal-upload-files') as CbarDialogElement | null;
}

function showUploadDialog(): void {
    const dialog = getUploadDialog();
    if (dialog) {
        dialog.open = true;
    }
}

function hideUploadDialog(): void {
    const dialog = getUploadDialog();
    if (dialog) {
        dialog.open = false;
    }
}

function submitUploadForm(): void {
    const form = getElementById<HTMLFormElement>('form_upload_document');
    if (!form) {
        return;
    }

    ensureFormCsrfToken(form);
    form.requestSubmit();
}

function bindUploadDialogDismiss(): void {
    const dialog = getUploadDialog();
    if (!dialog) {
        return;
    }

    dialog.querySelectorAll('cbar-button[data-dismiss="dialog"]').forEach((button) => {
        button.addEventListener('click', (event) => {
            event.preventDefault();
            hideUploadDialog();
        });
    });
}

function uploadButtonFromEvent(event: Event): HTMLElement | null {
    const path = event.composedPath?.() ?? [event.target as EventTarget];
    for (const node of path) {
        const el = node as Element;
        if (el?.classList?.contains('upload_file_btn')) {
            return el as HTMLElement;
        }
    }
    return null;
}

function openUploadDialogForButton(button: HTMLElement): void {
    const entityType = button.getAttribute('entity_type') ?? '';
    const entityId = button.getAttribute('entity_id') ?? '';

    void fetchJson<UploadPermissionResponse>('/ajax/getuploadfilepermission', {
        entity_type: entityType,
        entity_id: entityId,
    }).then((response) => {
        if (response.result !== 'success' || !response.data) {
            swal('Cannot upload files', response.message ?? '', 'error');
            return;
        }

        const entityName = getElementById('upload_entity_name');
        const elementTitle = getElementById('upload_element_title');
        if (entityName) {
            entityName.textContent = response.data.entity_name;
        }
        if (elementTitle) {
            elementTitle.innerHTML = response.data.entity_title;
        }

        getElementById<HTMLInputElement>('upload_entity_type')!.value = entityType;
        getElementById<HTMLInputElement>('upload_entity_id')!.value = entityId;

        const fileInput = getElementById<HTMLInputElement>('document_file');
        if (fileInput) {
            fileInput.value = '';
        }

        showUploadDialog();
    });
}

export function setupUploadFilesModal(): void {
    const dialog = getUploadDialog();
    if (!dialog) {
        return;
    }

    if (dialog.parentElement !== document.body) {
        document.body.appendChild(dialog);
    }

    dialog.open = false;

    void customElements.whenDefined('cbar-dialog').then(() => {
        bindUploadDialogDismiss();

        document.addEventListener('click', (event) => {
            const button = uploadButtonFromEvent(event);
            if (!button) {
                return;
            }
            event.preventDefault();
            openUploadDialogForButton(button);
        });

        getElementById('upload_file_submit_btn')?.addEventListener('click', () => {
            const fileInput = getElementById<HTMLInputElement>('document_file');
            if (!fileInput?.value) {
                window.alert('Please select a file');
                return;
            }
            if (window.confirm('Do you want to upload this document?')) {
                submitUploadForm();
            }
        });

        document.querySelectorAll<HTMLElement>('.remove_efe_btn').forEach((button) => {
            button.addEventListener('click', () => {
                if (!window.confirm('Do you want to remove this document?')) {
                    return;
                }

                const efeId = button.getAttribute('efe_id') ?? '';
                void fetchJson<RemoveEntityFileResponse>('/ajax/removeentityfileentity', {
                    efe_id: efeId,
                }).then((response) => {
                    if (response.result === 'success') {
                        document.getElementById(`efe_id_${efeId}`)?.remove();
                        return;
                    }
                    swal('Cannot remove document', response.message ?? '', 'error');
                });
            });
        });
    });
}
